import { useMe, useSettings } from '../../hooks/queries'
import { useTexte } from '../../texte'
import { Farbwege } from './Farbwege'
import type { HandbuchAbschnitt, HandbuchFuer } from '../../texte/handbuch'

/**
 * Die Dokumentation in den Einstellungen (#92).
 *
 * Aus dem Issue: ein einfaches Anwenderhandbuch, für Mitlesende, für
 * Autoren, für die Verwaltung und für den Supervisor -- „abrufbar in einem
 * Menü unter Einstellungen ‚Dokumentation‘, immer angepasst an die jeweiligen
 * Rechte."
 *
 * **Angepasst heißt: der Server sagt, wer was darf**, nicht die Rolle
 * (`may_create_trips`, `may_manage_people`, `may_operate`) -- dieselbe
 * Quelle wie für die Kategorien der Einstellungen selbst. Eine Verwaltung
 * liest alles, ein Mitlesender die Abschnitte für alle.
 *
 * **Und ein Dienst, der nicht eingerichtet ist, wird nicht erklärt.** Ohne
 * Apple gibt es kein Wetter, ohne Giphy keine GIFs -- ein Abschnitt darüber
 * beschriebe eine Anwendung, die es hier nicht gibt. Solange die Antwort
 * unterwegs ist, fehlen die Abschnitte und nicht die Seite.
 *
 * Der Wortlaut steht in `texte/handbuch.ts`; hier steht nur, was wann
 * erscheint.
 */
export function Handbuch() {
  const t = useTexte()
  const me = useMe()
  const settings = useSettings()
  const dienste = settings.data?.services

  const darf: Record<HandbuchFuer, boolean> = {
    alle: true,
    autor: me.data?.may_create_trips === true,
    verwaltung: me.data?.may_manage_people === true,
    betrieb: me.data?.may_operate === true,
  }
  const zeigen = (abschnitt: HandbuchAbschnitt): boolean =>
    darf[abschnitt.fuer] && (abschnitt.dienst === undefined || dienste?.[abschnitt.dienst] === true)

  const gruppen = (Object.keys(t.handbuch.gruppen) as HandbuchFuer[]).filter((g) => darf[g])

  return (
    <>
      <div className="card card--seite">
        <h2 className="card__titel">{t.handbuch.titel}</h2>
        <p className="card__lead card__lead--dicht">{t.handbuch.lead}</p>
      </div>

      {gruppen.map((gruppe) => (
        <section key={gruppe} data-fuer={gruppe}>
          {/* Mehr als eine Gruppe: dann trägt jede ihre Überschrift. Ein
              Mitlesender sieht nur eine, und „Für alle" über allem wäre eine
              Auskunft ohne Gegenstück. */}
          {gruppen.length > 1 && <p className="handbuch__gruppentitel">{t.handbuch.gruppen[gruppe]}</p>}
          {t.handbuch.abschnitte
            .filter((abschnitt) => abschnitt.fuer === gruppe && zeigen(abschnitt))
            .map((abschnitt) => (
              <div key={abschnitt.schluessel} className="card card--seite" data-abschnitt={abschnitt.schluessel}>
                <h2 className="card__titel">{abschnitt.titel}</h2>
                {abschnitt.absaetze.map((absatz, i) => (
                  <p key={i} className="handbuch__absatz">{absatz}</p>
                ))}
              </div>
            ))}
        </section>
      ))}

      {/* Die Farben als Tabelle -- unten, weil sie zwei Gruppen zugleich
          angehen.

          **Ein Mitlesender sieht sie nicht** (1.57.1, aus dem Betrieb: „nur
          für Autoren und Administratoren anzeigen"). Er kann ohnehin nur
          seine eigenen Farben ändern, und das steht unter *Darstellung*; die
          beiden anderen Wege sind für ihn eine Auskunft über Knöpfe, die es
          bei ihm nicht gibt. */}
      {(darf.autor || darf.verwaltung) && (
      <div className="card card--seite" data-abschnitt="farben">
        <h2 className="card__titel">{t.handbuch.farben.titel}</h2>
        <p className="card__lead card__lead--dicht">{t.handbuch.farben.lead}</p>
        {/* Erst das Bild, dann dieselben drei Wege als Text: die Zeichnung
            zeigt den Zusammenhang, die Zeilen nennen den vollen Weg und sind
            das, was ein Vorleseprogramm bekommt. */}
        <Farbwege />
        {t.handbuch.farben.zeilen.map((zeile) => (
          <p key={zeile.wer} className="schublade">
            <span className="schublade__name">{zeile.wer}</span>
            <span className="schublade__text">
              <strong>{zeile.was}</strong>
              <span className="handbuch__wo">{zeile.wo}</span>
            </span>
          </p>
        ))}
        <p className="handbuch__absatz">{t.handbuch.farben.dunkel}</p>
      </div>
      )}
    </>
  )
}
